/**
 * KnowledgeNode - Canvas node for Knowledge Space interaction
 *
 * Features:
 * - Displays documents from the board's knowledge space
 * - Upload button for adding new documents
 * - Question input with Ask button
 * - Streams answers to connected BlockNode OR shows inline
 * - Citations displayed with source references
 *
 * Design:
 * - Cyan/teal accent color (distinct from BlockNode blue and GeneratorNode purple)
 * - 400x500 default size, resizable
 * - Floating header with title input (shows on hover/select)
 */

import { memo, useState, useCallback, useRef, useEffect, type ChangeEvent } from 'react';
import { Handle, Position, NodeResizer, useStore, type NodeProps, type Node } from '@xyflow/react';
import type {
  KnowledgeNodeData,
  KnowledgeDocument,
  KnowledgeCitation,
} from '@deep-agents/shared-types';
import type * as Y from 'yjs';
import { useKnowledgeNode } from '../../hooks/useKnowledgeNode';

// ============================================================================
// TYPES
// ============================================================================

type KnowledgeNodeType = Node<KnowledgeNodeData, 'knowledge'>;

type KnowledgeNodeRuntimeData = KnowledgeNodeData & {
  nodesMap?: Y.Map<Node>;
  edgesMap?: Y.Map<any>;
  ydoc?: Y.Doc;
  makeChange?: (fn: () => void) => void;
};

// ============================================================================
// ICONS
// ============================================================================

const DocumentIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

const UploadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const LoadingIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" className="animate-spin">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.25" />
    <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ErrorIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function StatusBadge({ status }: { status: string }) {
  const config = {
    ready: { icon: <CheckIcon />, color: 'text-emerald-500', bg: 'bg-emerald-50' },
    processing: { icon: <LoadingIcon />, color: 'text-amber-500', bg: 'bg-amber-50' },
    queued: { icon: <LoadingIcon />, color: 'text-slate-400', bg: 'bg-slate-50' },
    failed: { icon: <ErrorIcon />, color: 'text-red-500', bg: 'bg-red-50' },
  }[status] || { icon: null, color: 'text-slate-400', bg: 'bg-slate-50' };

  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] ${config.color} ${config.bg}`}>
      {config.icon}
    </span>
  );
}

function DocumentItem({ doc }: { doc: KnowledgeDocument }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50/80 hover:bg-slate-100/80 transition-colors">
      <span className="text-cyan-600 flex-shrink-0">
        <DocumentIcon />
      </span>
      <span className="text-xs text-slate-700 truncate flex-1" title={doc.title}>
        {doc.title}
      </span>
      <StatusBadge status={doc.status} />
    </div>
  );
}

function CitationChip({
  citation,
  index,
}: {
  citation: KnowledgeCitation;
  index: number;
}) {
  return (
    <div className="flex items-start gap-2 p-2 rounded-lg bg-cyan-50/50 border border-cyan-100">
      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-cyan-100 text-cyan-700 text-[10px] font-semibold flex items-center justify-center">
        {index + 1}
      </span>
      <p className="text-[11px] text-slate-600 line-clamp-2">{citation.snippet}</p>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const KnowledgeNode = memo(({ id, data, selected, dragging }: NodeProps<KnowledgeNodeType>) => {
  const {
    title = '',
    boardId = '',
    nodesMap,
    edgesMap,
    ydoc,
    makeChange,
    onTitleChange,
  } = data as KnowledgeNodeRuntimeData;

  // ==================== DYNAMIC SHADOW ====================

  /**
   * Get shadow style based on node state
   * Matches all other nodes for consistency
   */
  const getShadowStyle = () => {
    if (dragging) {
      return 'var(--shadow-elevation-high)';
    }
    if (selected) {
      return 'var(--shadow-elevation-medium)';
    }
    return 'var(--shadow-elevation-low)';
  };

  // Local state
  const [localTitle, setLocalTitle] = useState(title);
  const [isHovered, setIsHovered] = useState(false);
  const [questionInput, setQuestionInput] = useState('');

  // File input ref
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Use the hook if we have all runtime props
  const hookEnabled = !!(boardId && nodesMap && edgesMap && ydoc && makeChange);

  const {
    space,
    documents,
    status,
    error,
    lastQuestion,
    lastAnswer,
    lastCitations,
    isOutputConnected,
    refreshDocuments,
    uploadDocument,
    ask,
  } = useKnowledgeNode({
    nodeId: id,
    boardId,
    nodesMap: nodesMap!,
    edgesMap: edgesMap!,
    ydoc: ydoc!,
    makeChange: makeChange!,
  });

  // Sync title with props
  useEffect(() => {
    setLocalTitle(title);
  }, [title]);

  // Handle title change
  const handleTitleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newTitle = e.target.value;
      setLocalTitle(newTitle);
      onTitleChange?.(newTitle);
    },
    [onTitleChange]
  );

  // Handle file upload
  const handleFileSelect = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file && hookEnabled) {
        await uploadDocument(file);
      }
      e.target.value = '';
    },
    [uploadDocument, hookEnabled]
  );

  // Handle ask
  const handleAsk = useCallback(async () => {
    if (!questionInput.trim() || !hookEnabled) return;
    const question = questionInput;
    setQuestionInput('');
    await ask(question);
  }, [questionInput, ask, hookEnabled]);

  // Handle enter key
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleAsk();
      }
    },
    [handleAsk]
  );

  // Auto-scroll to answer
  useEffect(() => {
    if (lastAnswer) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [lastAnswer]);

  // Control visibility
  const showControls = isHovered || selected;
  const isAsking = status === 'asking';
  const isLoading = status === 'loading';

  return (
    <div
      className="relative transition-all duration-300"
      style={{ padding: '0 8px' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Connection Handles */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="!w-4 !h-4 !bg-slate-400 !border-2 !border-white !opacity-100"
        style={{ top: '50%', pointerEvents: 'all', zIndex: 100 }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="!w-4 !h-4 !bg-cyan-500 !border-2 !border-white !opacity-100"
        style={{ top: '50%', pointerEvents: 'all', zIndex: 100 }}
      />

      {/* Floating Header */}
      <div
        className={`
          absolute bottom-full left-0 right-0 pb-2
          transition-opacity duration-200 z-30
          ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
      >
        <div className="flex items-center gap-2 px-1">
          <input
            type="text"
            value={localTitle}
            onChange={handleTitleChange}
            placeholder="Knowledge Space"
            className="nodrag nopan px-2.5 py-1 text-[12px] font-semibold text-cyan-700
              bg-gradient-to-r from-cyan-50 to-teal-50
              rounded-lg border border-cyan-100/50
              outline-none focus:border-cyan-300 focus:ring-1 focus:ring-cyan-200
              placeholder:text-cyan-300 placeholder:font-medium
              min-w-[80px] max-w-[200px] truncate transition-all"
            style={{ width: `${Math.max(80, Math.min(200, (localTitle.length || 12) * 8))}px` }}
          />
        </div>
      </div>

      {/* Main Card Container */}
      <div
        className="
          relative bg-white rounded-2xl overflow-hidden
          border transition-all duration-300
          border-neutral-200/60
        "
        style={{
          // Dynamic shadow based on drag/select state (matches all other nodes)
          boxShadow: getShadowStyle(),
          // Smooth shadow transition + scale effect
          transition: dragging
            ? 'box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            : 'box-shadow 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275), transform 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          // Slight scale up when dragging
          transform: dragging ? 'scale(1.01)' : 'scale(1)',
          width: 400,
          height: 500,
        }}
      >
        {/* Resize Handles */}
        <NodeResizer
          minWidth={320}
          minHeight={400}
          isVisible={selected}
          lineClassName="border-cyan-500"
          handleClassName="bg-cyan-500 border-2 border-white rounded"
        />

        {/* Content Area */}
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <span className="text-cyan-500">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>
              </span>
              <span className="text-sm font-medium text-slate-700">
                {space?.name || 'Board Knowledge'}
              </span>
            </div>
            <button
              type="button"
              onClick={() => hookEnabled && refreshDocuments()}
              disabled={isLoading}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
              title="Refresh documents"
            >
              <RefreshIcon />
            </button>
          </div>

          {/* Documents List */}
          <div className="flex-shrink-0 px-4 py-3 border-b border-slate-100 max-h-[140px] overflow-y-auto">
            <div className="space-y-1.5">
              {documents.length > 0 ? (
                documents.map((doc) => <DocumentItem key={doc.id} doc={doc} />)
              ) : (
                <p className="text-xs text-slate-400 text-center py-2">No documents yet</p>
              )}
            </div>

            {/* Upload Button */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={!hookEnabled || isLoading}
              className="nodrag nopan mt-2 w-full flex items-center justify-center gap-2 px-3 py-2
                rounded-lg border border-dashed border-cyan-200
                text-xs text-cyan-600 font-medium
                hover:bg-cyan-50 hover:border-cyan-300 transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UploadIcon />
              <span>Upload document</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.txt,.md,.doc,.docx"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Answer Area (inline when not connected) */}
          {!isOutputConnected && (lastAnswer || isAsking) && (
            <div className="flex-1 px-4 py-3 overflow-y-auto border-b border-slate-100">
              {lastQuestion && (
                <div className="mb-3">
                  <p className="text-[11px] text-slate-400 mb-1">You asked:</p>
                  <p className="text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-2">{lastQuestion}</p>
                </div>
              )}

              {isAsking ? (
                <div className="flex items-center gap-2 text-xs text-cyan-600">
                  <LoadingIcon />
                  <span>Thinking...</span>
                </div>
              ) : lastAnswer ? (
                <div>
                  <p className="text-[11px] text-slate-400 mb-1">Answer:</p>
                  <p className="text-xs text-slate-700 whitespace-pre-wrap">{lastAnswer}</p>

                  {lastCitations.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">Sources</p>
                      {lastCitations.map((citation, i) => (
                        <CitationChip key={i} citation={citation} index={i} />
                      ))}
                    </div>
                  )}
                </div>
              ) : null}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Spacer when connected */}
          {isOutputConnected && <div className="flex-1" />}

          {/* Question Input */}
          <div className="px-4 py-3">
            {error && (
              <div className="mb-2 px-3 py-2 bg-red-50 rounded-lg">
                <p className="text-xs text-red-600">{error}</p>
              </div>
            )}

            <div className="flex items-center gap-2">
              <input
                type="text"
                value={questionInput}
                onChange={(e) => setQuestionInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask a question..."
                disabled={!hookEnabled || isAsking}
                className="nodrag nopan flex-1 px-3 py-2 text-sm text-slate-700
                  bg-slate-50 rounded-lg border border-slate-200
                  outline-none focus:border-cyan-300 focus:ring-1 focus:ring-cyan-200
                  placeholder:text-slate-400
                  disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <button
                type="button"
                onClick={handleAsk}
                disabled={!hookEnabled || isAsking || !questionInput.trim()}
                className="nodrag nopan px-4 py-2 rounded-lg
                  bg-cyan-500 text-white text-sm font-medium
                  hover:bg-cyan-600 transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                  flex items-center gap-2"
              >
                {isAsking ? <LoadingIcon /> : <SendIcon />}
                <span>Ask</span>
              </button>
            </div>

            {isOutputConnected && (
              <p className="mt-2 text-[10px] text-cyan-600">
                Answer will stream to connected block
              </p>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
});

KnowledgeNode.displayName = 'KnowledgeNode';
